import Controller from '@ember/controller';
import moment from 'moment-timezone';

import { action, computed, set, get } from '@ember/object';

import { task, timeout } from 'ember-concurrency';

import { inject as iService } from '@ember/service';

import { inject as iController } from '@ember/controller';

import getConfig from 'beebee-base/lib/get-config';
import { beebeeAjaxGet } from 'beebee-base/lib/utility';
import { beebeeAjaxPost } from 'beebee-base/lib/utility/ajax';
import defaultComprensorio from 'e-commerce/lib/default-comprensorio';

/**
 * MAP (in-file navigation graph — any LLM reads this without external tooling;
 * keep in sync with the code):
 *   role:      controller — instructor-first private-lesson booking page: pick an
 *              instructor, then a product/timeslot, then price and add to cart. A
 *              near-twin of detail.js's private-lesson flow, entered from an
 *              instructor's own page instead of a product's.
 *   extends:   Controller (@ember/controller)
 *   template:  none
 *   qp:        -
 *   key:       loadInstructorTask (/ecommerce_maestro_detail/:id, redirects home if
 *              missing), freePrivatePriceTask (/scuolasci_freeprice), checkBlockTask
 *              (/ecommerce_check_product before cart add), selectProduct (@action, sets
 *              buyMaestro/item/intComp from the chosen product), addParPrivate/
 *              delParPrivata (@action, participant-count +/-), addPrivateCart/
 *              _addPrivateCart (cart write via applicationController.addCart).
 *   uses:      applicationController (@controller('application') — location/cart via
 *              addCart), lib/get-config, lib/utility (beebeeAjaxGet), lib/utility/ajax
 *              (beebeeAjaxPost), lib/default-comprensorio (resort the cart records)
 *   siblings:  routes/../ (no dedicated route file — wired via simple-routes-test.js
 *              coverage of `route:ski-instructor`), detail.js (source of the ported
 *              freePrivatePriceTask/checkBlockTask/_addPrivateCart/addParPrivate/
 *              delParPrivata logic for the instructor-first entry point)
 *   tests:     ski-instructor-test.js
 *   gotchas:   freePrivatePriceTask guards `product?.events` before iterating — an
 *              empty `products` array in the /scuolasci_freeprice response left
 *              `product` undefined (twin of Sentry EMBER-SCUOLE-ECOMMERCE-2XK, fixed
 *              in detail.js's copy of this same task).
 *
 * Instructor-first entry point into the same private-lesson add-to-cart flow
 * detail.js exposes product-first; keep the two freePrivatePriceTask/
 * checkBlockTask copies in sync when fixing one.
 */
export default class SkiInstructorController extends Controller {
  @iService notify;
  @iService intl;
  @iService session;
  @iService store;
  @iService router;

  @iController('application') applicationController;
  @iService log;

  @(task(function* (lang = null) {
    yield timeout(250);

    try {
      const instructor = yield beebeeAjaxGet(
        `${getConfig(this).APP.urlApi}/ecommerce_maestro_detail/${this.instructor_id}`
      );

      if (!instructor || !instructor.id) {
        this.router.transitionTo('index');
        return;
      }

      set(this, 'instructor', instructor);
    } catch (error) {
      this.router.transitionTo('index');
    }
  }).restartable())
  loadInstructorTask;

  @computed('instructor.id', 'this.session.openOrganization.id')
  get showSkeleton() {
    const { applicationController } = this;
    const { open_organization } = applicationController?.content || {};

    return !open_organization?.get('id') || !this.instructor?.id;
  }

  @computed('this.session.openOrganization.thumbnail_url', 'urlData')
  get thumbnail_url() {
    if (get(this, 'session.openOrganization.thumbnail_url')) {
      return `${this.urlData}/${get(this, 'session.openOrganization.thumbnail_url')}-medium.jpeg`;
    }
    return 'https://beebeeassets.beebeeboard.com/assets/img/logo.png';
  }

  @computed(
    'applicationController.imgPrivate',
    'applicationController.imgCorsi',
    'instructor.thumbnail_url',
    'this.session.openOrganization.id'
  )
  get imgCopertina() {
    const { applicationController, instructor } = this;
    const { open_organization } = applicationController?.content || {};

    if (!open_organization?.get('id')) {
      return null;
    }

    if (instructor.thumbnail_url) {
      return `${applicationController.urlData}/${instructor.thumbnail_url}-large.jpeg`;
    }

    return applicationController.imgPrivate;
  }

  @(task(function* () {
    try {
      yield timeout(50);
      const self = this;
      const events = [];

      yield timeout(500);

      if (this.oreSelected.length > 0) {
        for (const e of this.oreSelected) {
          if (!e.n_pax) {
            set(e, 'n_pax', 1);
          }

          const start_date = moment(e.start_date).tz('Europe/Rome', true).format('YYYY-MM-DDTHH:mmZ');
          const end_date = moment(e.end_date).tz('Europe/Rome', true).format('YYYY-MM-DDTHH:mmZ');

          events.push({
            partecipanti: '1',
            fisso: false,
            maestro_id: this.instructor_id,
            comprensorio: this.buyMaestro.product.comprensorio,
            lingua: self.language || '',
            persone: e.n_pax,
            start_date,
            end_date,
          });
        }
      }

      const data = {
        products: [
          {
            thumbnail_url: 't',
            partecipanti: '1',
            name: 'Individuale',
            persone: 0,
            product_status_id: 92,
            id: this.buyMaestro.product.id,
            assicurazione: false,
            events: events,
            modules: [1],
          },
        ],
        access_token: self.session.token,
      };

      const prezzoAPI = yield beebeeAjaxPost(
        `${getConfig(this).APP.urlApi}/scuolasci_freeprice`,
        null,
        data
      );

      if (Array.isArray(prezzoAPI?.data?.products)) {
        const product = prezzoAPI.data.products[0];
        // products can be an empty array: product is then undefined and
        // product.events threw (twin of Sentry EMBER-SCUOLE-ECOMMERCE-2XK,
        // fixed in detail.js). Guard both hops.
        (product?.events || []).forEach((event) => {
          if (self.oreSelected.length > 0) {
            self.oreSelected.forEach((e) => {
              if (
                moment(e.start_date)
                  .tz('Europe/Rome', true)
                  .isSame(moment(event.start_date).tz('Europe/Rome'), 'minute')
              ) {
                set(e, 'price', event.prezzo);
              }
            });
          }
        });
      }

      set(this, 'oreSelected.prezzoAPI', prezzoAPI?.data);

      if (
        prezzoAPI.data.products &&
        prezzoAPI.data.products.some(
          (product) => product.product_status_id === 92 && product.maestro_diverso === true
        )
      ) {
        this.notify.warning(this.intl.t('We cannot guarantee the same teacher'));
        set(this, 'oreSelected', []);
        set(this, 'buyMaestro', null);
        return false;
      }

      return true;
    } catch (e) {
      // Left uncaught, a 4xx/network failure here surfaced as a Sentry
      // unhandled rejection. User-triggered (private price calc): notify.
      this.log.error('freePrivatePriceTask failed', e);
      const detail = e?.responseJSON?.errors?.[0]?.detail;
      this.notify.error(detail || this.intl.t('beebeeboard error'));
      return false;
    }
  }).restartable())
  freePrivatePriceTask;

  @(task(function* (dispo = null, individuali = false) {
    yield timeout(50);

    try {
      yield beebeeAjaxPost(
        `${getConfig(this).APP.urlApi}/ecommerce_check_product`,
        this.session.token,
        this.item.block_check_json
      );

      if (individuali) {
        this._addPrivateCart();
      }
    } catch (e) {
      this.log.error('ski-instructor: add private cart failed', e);
    }
  }).restartable())
  checkBlockTask;

  // <PrivateCalendar> resolves the resort it shows on its own and reports it back;
  // mirror it so the cart names the resort the customer is actually looking at.
  @action
  publishComp(comp) {
    if (!comp || comp === this.intComp) {
      return;
    }

    set(this, 'intComp', comp);
  }

  _addPrivateCart() {
    const bloccoOre = {
      maestro: this.buyMaestro,
      item: this.item,
      // Same trap as controllers/detail.js, and the same order: the hour carries
      // the resort <PrivateCalendar> actually fetched availability for. `intComp`
      // comes from the chosen product's `comprensorio` and can be empty, `location`
      // is only ever set by the detail page, and this route is reached by deep link,
      // so neither can be trusted on its own (POP-109).
      comprensorio:
        this.oreSelected?.[0]?.location ||
        this.intComp ||
        this.applicationController.location ||
        defaultComprensorio(this.session.option_field, this.buyMaestro),
      language: this.intLang,
      gender: this.gender,
      prezzoAPI: this.oreSelected.prezzoAPI,
      ore: [],
    };

    for (let i = 0; i < this.oreSelected.length; i++) {
      bloccoOre.ore.push(this.oreSelected[i]);
    }

    this.applicationController.addCart('private', bloccoOre);
    set(this, 'buyMaestro', null);
  }

  @action
  addPrivateCart() {
    this._addPrivateCart();
  }

  get minPartecipanti() {
    return this.session.option_field?.ecommerce_private_min_max?.min || 1;
  }

  get maxPartecipanti() {
    return this.session.option_field?.ecommerce_private_min_max?.max || 10;
  }

  // Ported from detail controller: template referenced these string actions
  // but they were never defined here (clicks used to throw).
  @action
  addParPrivate() {
    for (let i = 0; i < this.oreSelected.length; i++) {
      if (this.oreSelected[i].n_pax) {
        set(this.oreSelected[i], 'n_pax', this.oreSelected[i].n_pax + 1);
      }

      if (!this.oreSelected[i].n_pax || this.oreSelected[i].n_pax > this.maxPartecipanti) {
        set(this.oreSelected[i], 'n_pax', this.maxPartecipanti);
      }
    }

    this.freePrivatePriceTask.perform();
  }

  @action
  delParPrivata() {
    for (let i = 0; i < this.oreSelected.length; i++) {
      if (this.oreSelected[i].n_pax) {
        set(this.oreSelected[i], 'n_pax', this.oreSelected[i].n_pax - 1);
      }

      if (!this.oreSelected[i].n_pax || this.oreSelected[i].n_pax < this.minPartecipanti) {
        set(this.oreSelected[i], 'n_pax', this.minPartecipanti);
      }
    }

    this.freePrivatePriceTask.perform();
  }

  @action
  selectProduct(product) {
    const { applicationController } = this;
    const { prodotti } = applicationController || {};
    const { products } = prodotti || {};

    let item = null;
    if (Array.isArray(products)) {
      item = products.find((i) => i.id === parseInt(product.id));
    }

    // Handle the product action
    set(this, 'buyMaestro', {
      product,
      id: this.instructor.id,
    });
    set(this, 'start_date', product.first_dispo);
    set(this, 'intComp', product.comprensorio);
    set(this, 'item', item);
  }
}
